import React, { useMemo, useState } from 'react';
import {
    Card,
    CardBody,
    CardFooter,
    CardDivider,
    CardLayout,
    DateFormat,
    Button,
    ActionsLayout,
    CardText,
    CardSubtext
} from '@crx-dev/hss-react-components';
import ReceivedInfo from './components/ReceivedInfo';
import { Received } from './types/EntryTypes/Received';
import NoteForm from '../../Common/forms/NoteForm';
import { useBooleanState, useWindowSize } from '@crx-dev/hss-react-infrastructure/dist';
import { Entry } from './types/Entry';
import SuppliedInfo from './components/SuppliedInfo';
import { Supplied } from './types/EntryTypes/Supplied';
import QuarantinedInfo from './components/QuarantinedInfo';
import { Quarantined } from './types/EntryTypes/Quarantined';
import DestroyedInfo from './components/DestroyedInfo';
import { Destroyed } from './types/EntryTypes/Destroyed';
import { Amendment } from './types/EntryTypes/Amendment';
import AmendmentInfo from './components/AmendmentInfo';
import StockCheckInfo from './components/StockCheckInfo';
import { StockCheck } from './types/EntryTypes/StockCheck';
import { useHistory } from 'react-router-dom';
import { makeReturnUrl } from '../../../Infrastructure/pageState/useMakeReturnUrl';

interface EntriesListItemProps {
    item: Entry;
    nestedItem?: Entry;
}

function EntriesListItem({ item, nestedItem }: EntriesListItemProps) {
    const [showingAddNoteForm, showAddNoteForm, hideAddNoteForm] = useBooleanState(false);
    const { width } = useWindowSize();
    const [showMore, setShowMore] = useState(false);
    const history = useHistory();

    const columns = useMemo(() => {
        if (width > 800) return 4;
        else return 2;
    }, [width]);

    return (
        <>
            <Card>
                <CardBody>
                    {item.controlDrugRegisterEntryType === 'Received' && (
                        <ReceivedInfo columns={columns} item={item as Received} />
                    )}
                    {item.controlDrugRegisterEntryType === 'Supplied' && (
                        <SuppliedInfo columns={columns} item={item as Supplied} />
                    )}
                    {item.controlDrugRegisterEntryType === 'Quarantined' && (
                        <QuarantinedInfo columns={columns} item={item as Quarantined} />
                    )}
                    {item.controlDrugRegisterEntryType === 'Destroyed' && (
                        <DestroyedInfo columns={columns} item={item as Destroyed} />
                    )}
                    {item.controlDrugRegisterEntryType === 'Amendment' && (
                        <>
                            <AmendmentInfo columns={columns} item={item as Amendment} />

                            {nestedItem !== undefined && (
                                <>
                                    {showMore && (
                                        <>
                                            <CardDivider />

                                            <Card mt="l" nested>
                                                <CardBody>
                                                    {nestedItem?.controlDrugRegisterEntryType === 'Received' && (
                                                        <ReceivedInfo columns={columns} item={nestedItem as Received} />
                                                    )}
                                                    {nestedItem?.controlDrugRegisterEntryType === 'Supplied' && (
                                                        <SuppliedInfo columns={columns} item={nestedItem as Supplied} />
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {item.controlDrugRegisterEntryType === 'StockCheck' && (
                        <StockCheckInfo columns={columns} item={item as StockCheck} />
                    )}
                    {item.notes && item.notes.length > 0 && (
                        <>
                            <CardDivider />

                            <CardLayout columns={1}>
                                {item.notes.map(note => (
                                    <Card key={note.id} nested background="BertYellow">
                                        <CardBody>
                                            <CardText mb="none">
                                                {note &&
                                                    note.note.split('\n').map((text, index) => (
                                                        <>
                                                            {index > 0 && <br />}
                                                            {text}
                                                        </>
                                                    ))}
                                            </CardText>
                                            <CardSubtext>
                                                Added by {note.addedBy} at{' '}
                                                <DateFormat date={note.createdDateTime} inline />
                                            </CardSubtext>
                                        </CardBody>
                                    </Card>
                                ))}
                            </CardLayout>
                        </>
                    )}
                </CardBody>
                <CardFooter>
                    <ActionsLayout layout="end">
                        {nestedItem !== undefined && (
                            <Button
                                id={`${item.id}-ShowHistory`}
                                size="small"
                                onClick={() => setShowMore(!showMore)}
                                variant="tertiary"
                            >
                                {showMore ? <>Hide History</> : <> Show History</>}
                            </Button>
                        )}

                        <Button id={`${item.id}-AddNote`} size="small" onClick={showAddNoteForm} variant="tertiary">
                            Add Note
                        </Button>
                        {(item.controlDrugRegisterEntryType === 'Supplied' ||
                            item.controlDrugRegisterEntryType === 'Received') && (
                            <Button
                                id={`${item.id}-MakeAmendment`}
                                size="small"
                                onClick={() =>
                                    history.push(
                                        `/registers/${
                                            item.registerId
                                        }/amendment?returnUrl=${makeReturnUrl()}&amendedFor=${item.id}`
                                    )
                                }
                                variant="tertiary"
                            >
                                Make Amendment
                            </Button>
                        )}
                    </ActionsLayout>
                </CardFooter>
            </Card>
            {showingAddNoteForm && (
                <NoteForm
                    registerId={item.registerId}
                    entryId={item.id}
                    onCancel={hideAddNoteForm}
                    type="controldrugregister"
                    queryKey="controlled-entries"
                />
            )}
        </>
    );
}

export default EntriesListItem;
